import React from 'react';
import {
  Box,
  Paper,
  Typography,
  LinearProgress,
  Chip,
  Grid,
  alpha,
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  Star as StarIcon,
} from '@mui/icons-material';
import { InvestmentScore } from '../../../types/chat';

interface InvestmentScoreCardProps {
  investmentScore: InvestmentScore;
}

const getGradeColor = (grade: string) => {
  switch (grade) {
    case 'A':
      return '#4caf50'; // green
    case 'B':
      return '#8bc34a'; // light green
    case 'C':
      return '#ffc107'; // amber
    case 'D':
      return '#ff9800'; // orange
    case 'F':
      return '#f44336'; // red
    default:
      return '#9e9e9e'; // grey
  }
};

const getRecommendationColor = (recommendation: string) => {
  const rec = recommendation.toLowerCase();
  if (rec.includes('strong buy')) return 'success';
  if (rec.includes('buy')) return 'info';
  if (rec.includes('hold')) return 'warning';
  if (rec.includes('caution') || rec.includes('pass')) return 'error';
  return 'default';
};

const InvestmentScoreCard: React.FC<InvestmentScoreCardProps> = ({ investmentScore }) => {
  const gradeColor = getGradeColor(investmentScore.grade);

  return (
    <Paper
      elevation={3}
      sx={{
        p: 3,
        my: 2,
        background: `linear-gradient(135deg, ${alpha(gradeColor, 0.1)} 0%, ${alpha(
          gradeColor,
          0.05
        )} 100%)`,
        border: `2px solid ${gradeColor}`,
        borderRadius: 3,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <StarIcon sx={{ color: gradeColor, mr: 1, fontSize: 28 }} />
        <Typography variant="h6" fontWeight="bold">
          Investment Analysis
        </Typography>
      </Box>

      <Grid container spacing={3}>
        {/* Grade Display */}
        <Grid item xs={12} sm={4}>
          <Box sx={{ textAlign: 'center' }}>
            <Box
              sx={{
                width: 80,
                height: 80,
                borderRadius: '50%',
                bgcolor: gradeColor,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto',
                mb: 1,
              }}
            >
              <Typography
                variant="h2"
                sx={{
                  color: 'white',
                  fontWeight: 'bold',
                }}
              >
                {investmentScore.grade}
              </Typography>
            </Box>
            <Typography variant="caption" color="text.secondary">
              Investment Grade
            </Typography>
          </Box>
        </Grid>

        {/* Score Display */}
        <Grid item xs={12} sm={4}>
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h2" sx={{ color: gradeColor, fontWeight: 'bold', mb: 1 }}>
              {investmentScore.score}
            </Typography>
            <LinearProgress
              variant="determinate"
              value={investmentScore.score}
              sx={{
                height: 8,
                borderRadius: 4,
                bgcolor: alpha(gradeColor, 0.2),
                '& .MuiLinearProgress-bar': {
                  bgcolor: gradeColor,
                },
              }}
            />
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              Overall Score (0-100)
            </Typography>
          </Box>
        </Grid>

        {/* Recommendation */}
        <Grid item xs={12} sm={4}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
            <Chip
              icon={<TrendingUpIcon />}
              label={investmentScore.recommendation}
              color={getRecommendationColor(investmentScore.recommendation) as 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning'}
              sx={{
                fontSize: '0.9rem',
                fontWeight: 'bold',
                px: 1,
                mb: 1,
              }}
            />
            <Typography variant="caption" color="text.secondary">
              Recommendation
            </Typography>
          </Box>
        </Grid>

        {/* Breakdown (if available) */}
        {investmentScore.breakdown && (
          <Grid item xs={12}>
            <Box sx={{ mt: 1 }}>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                Score Breakdown
              </Typography>
              <Grid container spacing={1}>
                {investmentScore.breakdown.marketPosition !== undefined && (
                  <Grid item xs={6} sm={3}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="body2" fontWeight="bold">
                        {investmentScore.breakdown.marketPosition}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Market Position
                      </Typography>
                    </Box>
                  </Grid>
                )}
                {investmentScore.breakdown.financialHealth !== undefined && (
                  <Grid item xs={6} sm={3}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="body2" fontWeight="bold">
                        {investmentScore.breakdown.financialHealth}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Financial Health
                      </Typography>
                    </Box>
                  </Grid>
                )}
                {investmentScore.breakdown.valuationROI !== undefined && (
                  <Grid item xs={6} sm={3}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="body2" fontWeight="bold">
                        {investmentScore.breakdown.valuationROI}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Valuation & ROI
                      </Typography>
                    </Box>
                  </Grid>
                )}
                {investmentScore.breakdown.riskLevel !== undefined && (
                  <Grid item xs={6} sm={3}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="body2" fontWeight="bold">
                        {investmentScore.breakdown.riskLevel}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Risk Level
                      </Typography>
                    </Box>
                  </Grid>
                )}
              </Grid>
            </Box>
          </Grid>
        )}
      </Grid>
    </Paper>
  );
};

export default InvestmentScoreCard;
